import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, ExternalLink, TrendingUp, DollarSign } from 'lucide-react';
import { featuredProducts } from '../../data/mock';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const trackClick = async (product) => {
  try {
    await fetch(`${API_URL}/api/clicks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_id: product.id.toString(),
        product_name: product.name,
        affiliate_network: product.affiliateNetwork,
        commission_amount: product.commissionAmount
      })
    });
  } catch (error) {
    console.error('Click tracking failed:', error);
  }
};

export const BestAffiliateProducts2025 = () => {
  const topProducts = featuredProducts.slice(0, 3);

  const handleProductClick = (product) => {
    trackClick(product);
    window.open(product.affiliateLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto max-w-4xl px-4 py-24">
        <Link to="/blog" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        {/* Article Header */}
        <article>
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <span>Published: January 15, 2025</span>
              <span>•</span>
              <span>8 min read</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Top 10 Highest Paying Affiliate Products in 2025
            </h1>
            <p className="text-xl text-gray-600">
              After promoting hundreds of affiliate products, I've narrowed down the absolute best ones that actually pay and convert. Here's what's working right now.
            </p>
          </div>

          <img 
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80" 
            alt="Affiliate Marketing Dashboard"
            className="w-full h-96 object-cover rounded-lg mb-12"
          />

          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-6">
              Look, I'm going to be brutally honest with you. Most "best affiliate products" lists are garbage. They recommend products the author has never even used, let alone made money with.
            </p>
            <p className="text-lg text-gray-700 mb-6">
              This list is different. These are products I've personally promoted (or watched other successful affiliates crush with). Every product here has:
            </p>
            <ul className="list-disc pl-6 mb-8 text-gray-700 space-y-2">
              <li>Commissions of $100+ per sale (most are $200-$500)</li>
              <li>Conversion rates above 5% (some hit 10%+)</li>
              <li>Active marketing support from the vendor</li>
              <li>Low refund rates (under 10%)</li>
              <li>Proven track record on ClickBank</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Reality of Affiliate Marketing in 2025</h2>
            <p className="text-lg text-gray-700 mb-6">
              Here's what nobody tells you: Most affiliates fail because they promote garbage products with 2% conversion rates and $20 commissions. You need 500 sales to make $10K. That's brutal.
            </p>
            <p className="text-lg text-gray-700 mb-6">
              But promote a $500 commission product with a 7% conversion rate? You only need 20 sales. That's doable.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Top 3 Products (My Personal Recommendations)</h2>
          </div>

          {/* Product Showcases */}
          <div className="space-y-8 my-12">
            {topProducts.map((product, index) => (
              <div key={product.id} className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-emerald-600 font-bold text-lg">#{index + 1}</span>
                    <h3 className="text-2xl font-bold text-gray-900 mt-2">{product.name}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-emerald-600">${product.commissionAmount}</div>
                    <div className="text-sm text-gray-500">per sale</div>
                  </div>
                </div>

                <p className="text-gray-700 mb-6">{product.description}</p>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Conversion Rate</div>
                    <div className="text-2xl font-bold text-gray-900">{product.conversionRate}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Commission</div>
                    <div className="text-2xl font-bold text-gray-900">{product.commission}%</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Gravity Score</div>
                    <div className="text-2xl font-bold text-gray-900">{product.gravity}</div>
                  </div>
                </div>

                <Button 
                  onClick={() => handleProductClick(product)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
                >
                  Get Your Affiliate Link <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </div>
            ))}
          </div>

          {/* Conclusion */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How to Actually Make Money With These</h2>
            <p className="text-lg text-gray-700 mb-6">
              Having good products is only half the battle. Here's the brutal truth:
            </p>
            <ul className="list-disc pl-6 mb-8 text-gray-700 space-y-2">
              <li><strong>You need traffic.</strong> Reddit, Facebook groups, Instagram - pick ONE and go all in.</li>
              <li><strong>Focus on one product.</strong> Don't spread yourself thin promoting 10 different things.</li>
              <li><strong>Provide value first.</strong> Help people, then recommend products. Don't just spam links.</li>
              <li><strong>Track everything.</strong> Know which traffic sources convert. Double down on what works.</li>
            </ul>

            <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 my-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Want the Full List?</h3>
              <p className="text-gray-700 mb-4">See all 10 products with detailed breakdowns, promotional strategies, and my personal earnings data.</p>
              <Link to="/">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  View All Products →
                </Button>
              </Link>
            </div>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};export default BestAffiliateProducts2025;
